import { memo, useCallback } from 'react';
import { Paper, Typography, Box, Button, Chip, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { QueryGroup, QueryField, QueryRule as QueryRuleType } from '../../../../types/QueryBuilder';
import { alpha } from '@mui/material/styles';
import { useLanguage } from '../../../../context/LanguageContext';
import { primarySoft } from '../../../../theme';
import QueryRule from './QueryRule';

interface QueryRuleGroupProps {
    group: QueryGroup;
    categories: string[];
    fields: QueryField[];
    onAddRule: (groupId: string) => void;
    onUpdateRule: (groupId: string, ruleId: string, data: Partial<QueryRuleType>) => void;
    onRemoveRule: (groupId: string, ruleId: string) => void;
    onRemoveGroup?: (groupId: string) => void;
    showCombinator?: boolean;
    canRemoveGroup?: boolean;
}

const QueryRuleGroup = memo(function QueryRuleGroup({
    group,
    categories,
    fields,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
    onRemoveGroup,
    showCombinator = false,
    canRemoveGroup = false,
}: QueryRuleGroupProps) {
    const { t } = useLanguage();
    const groupId = group.id;
    const handleUpdateRule = useCallback(
        (ruleId: string, data: Partial<QueryRuleType>) => onUpdateRule(groupId, ruleId, data),
        [onUpdateRule, groupId]
    );
    const handleRemoveRule = useCallback((ruleId: string) => onRemoveRule(groupId, ruleId), [onRemoveRule, groupId]);

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('pages.clients.queryBuilder.ruleGroup')}
                    </Typography>
                    {showCombinator && (
                        <Chip label={group.combinator} size="small" color="primary" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'common.white' }} />
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => onAddRule(groupId)}
                        sx={{ textTransform: 'none', color: 'primary.main', '&:hover': { backgroundColor: (t) => primarySoft(t, 0.08) } }}
                    >
                        {t('pages.clients.queryBuilder.addRule')}
                    </Button>

                    {canRemoveGroup && onRemoveGroup && (
                        <Tooltip title={t('pages.clients.queryBuilder.deleteGroup')}>
                            <IconButton
                                size="small"
                                onClick={() => onRemoveGroup(groupId)}
                                sx={{ color: 'error.main', '&:hover': { backgroundColor: (t) => alpha(t.palette.error.main, 0.1) } }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {group.rules.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', bgcolor: 'background.paper', borderRadius: 1, border: '2px dashed', borderColor: 'divider' }}>
                    <Typography variant="body2">{t('pages.clients.queryBuilder.noRulesMessage')}</Typography>
                </Box>
            ) : group.rules.map((rule, index) => (
                <Box key={rule.id}>
                    <QueryRule
                        rule={rule}
                        categories={categories}
                        fields={fields}
                        onUpdateRule={handleUpdateRule}
                        onRemoveRule={handleRemoveRule}
                    />
                    {index < group.rules.length - 1 && (
                        <Box sx={{ textAlign: 'center', my: 1 }}>
                            <Chip
                                label={t('pages.clients.queryBuilder.and')}
                                size="small"
                                sx={{ bgcolor: 'success.main', color: 'common.white', fontWeight: 600, fontSize: '0.75rem' }}
                            />
                        </Box>
                    )}
                </Box>
            ))}
        </Paper>
    );
});

export default QueryRuleGroup;
